import React from "react";
import styled from "styled-components";
import { Card, Table, Tag, Button, Empty } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import TruncatedText from "../../../shared/components/TruncatedText.jsx";

const QueueContainer = styled.div`
  margin-bottom: 24px;
`;

const QueueCard = styled(Card)`
  .ant-card-head-title {
    font-weight: 600;
    color: #005249;
  }
`;

const QueueTable = styled(Table)`
  .ant-table-tbody > tr {
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f5f5f5;
    }
  }
`;

const ExceptionFirstQueue = ({ exceptionQueue, loading }) => {
  const navigate = useNavigate();

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "CRITICAL":
        return "error";
      case "HIGH":
        return "error";
      case "MEDIUM":
        return "warning";
      case "LOW":
        return "default";
      default:
        return "default";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "INCIDENT":
        return "red";
      case "EXCEPTION":
        return "orange";
      case "PAYOUT":
        return "blue";
      case "TRUST":
        return "purple";
      default:
        return "default";
    }
  };

  const formatAge = (hours) => {
    if (hours < 24) {
      return `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (type) => (
        <Tag color={getTypeColor(type)} style={{ fontWeight: 600 }}>
          {type}
        </Tag>
      ),
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      width: 100,
      render: (severity) => (
        <Tag color={getSeverityColor(severity)} style={{ fontWeight: 600 }}>
          {severity}
        </Tag>
      ),
    },
    {
      title: "Entity",
      dataIndex: "entityId",
      key: "entityId",
      width: 120,
      render: (text) => (
        <code style={{ fontSize: "11px", color: "#8c8c8c" }}>{text}</code>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (text) => <TruncatedText text={text} maxLength={40} />,
    },
    {
      title: "Age",
      dataIndex: "ageHours",
      key: "ageHours",
      width: 80,
      render: (hours) => (
        <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
          {formatAge(hours)}
        </span>
      ),
    },
    {
      title: "Next Step",
      dataIndex: "suggestedNextStep",
      key: "suggestedNextStep",
      ellipsis: true,
      render: (text) => (
        <span style={{ fontSize: "12px", color: "#666" }}>
          {text}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(record.deepLink);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const handleRowClick = (record) => {
    navigate(record.deepLink);
  };

  return (
    <QueueContainer>
      <QueueCard
        title="Exception-First Queue"
        extra={
          exceptionQueue?.length > 0 && (
            <Tag color="red">{exceptionQueue.length} items</Tag>
          )
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            Loading...
          </div>
        ) : !exceptionQueue || exceptionQueue.length === 0 ? (
          <Empty
            description="No exceptions requiring attention"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <QueueTable
            columns={columns}
            dataSource={exceptionQueue}
            rowKey="id"
            pagination={false}
            size="small"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
            })}
            scroll={{ x: 800 }}
          />
        )}
      </QueueCard>
    </QueueContainer>
  );
};

export default ExceptionFirstQueue;
